import React, { useEffect, useRef } from 'react';
import { MockAIService } from '../services/AIService';
import { RealVoiceService } from '../services/VoiceService';

interface TypingGuidanceProps {
    currentWord: string;
    currentLetter: string;
    aiService: MockAIService;
    voiceService: RealVoiceService;
    wrongKey?: string;
    guidanceCancellationRef?: React.MutableRefObject<{ cancelled: boolean } | null>;
    addDebugLog?: (message: string) => void;
    guidanceTrigger?: number;
    isEncouragementPlaying?: boolean;
}

export const TypingGuidance: React.FC<TypingGuidanceProps> = ({ 
    currentWord, 
    currentLetter, 
    aiService, 
    voiceService, 
    wrongKey,
    guidanceCancellationRef,
    addDebugLog,
    guidanceTrigger,
    isEncouragementPlaying
}) => {
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const lastLetterRef = useRef<string>('');
    const lastWordRef = useRef<string>('');
    const lastTriggerRef = useRef<number>(0);
    
    // Detailed logging helper with millisecond timestamps
    const logDetailed = (message: string) => {
        const timestamp = new Date().toISOString().slice(11, 23); // HH:MM:SS.mmm
        if (addDebugLog) {
            addDebugLog(`[${timestamp}] ${message}`);
        }
    };

    useEffect(() => {
        const speakGuidance = async () => {
            try {
                // Use the enhanced playTypingGuidance method that handles audio + TTS
                await aiService.playTypingGuidance(currentLetter, voiceService, wrongKey);
            } catch (error) {
                console.error('Failed to speak guidance:', error);
                // Final fallback - use basic TTS
                const guidance = aiService.getTypingGuidance(currentLetter, wrongKey);
                await voiceService.speak(guidance);
            }
        };

        // Reset tracking when word changes (new word started)
        if (currentWord !== lastWordRef.current) {
            lastWordRef.current = currentWord;
            lastLetterRef.current = '';
            lastTriggerRef.current = 0;
            return;
        }
        
        // Handle wrong key - play guidance immediately
        if (wrongKey) {
            speakGuidance();
            return;
        }

        // Handle guidance trigger (fired after encouragement completes)
        if (guidanceTrigger && guidanceTrigger > 0 && guidanceTrigger !== lastTriggerRef.current) {
            lastTriggerRef.current = guidanceTrigger;
            
            if (currentLetter) {
                speakGuidance();
            }
        }
    }, [currentWord, currentLetter, wrongKey, aiService, voiceService, guidanceTrigger]);

    return null;
};